import React, { useState, useEffect, useRef } from 'react';
import { colors as theme } from '../theme';
import { toastError } from '../ui/toast';
import { tabForReasons } from '../utils/needsYouToday';

// "Needs you" at the top of Manage Clients (plan 004): the coach's clients who need them, why, and a
// Clear button that hides a client until something newer happens. `needs` comes from useNeedsYou.
const c = {
  card: theme.surface1, border: 'rgba(42, 53, 98, 0.8)', text: theme.text, muted: theme.textMuted,
  second: '#C0C8DC', gold: theme.gold, danger: theme.danger, success: theme.success,
};
const reasonIcon = { message: '💬', new: '👋', 'check-in': '📋', flag: '🚩', idle: '⏸' };
const UNDO_SECONDS = 8;

const NeedsYouGroup = ({ needs, onOpen }) => {
  const [busy, setBusy] = useState(null);          // the client being cleared
  const [justCleared, setJustCleared] = useState(null); // { saved, name } for Undo
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  if (!needs) return null;
  const { rows, loading, failed, reload, clear, undo } = needs;

  const onClear = async (client) => {
    setBusy(client.auth_user_id);
    try {
      const saved = await clear(client.auth_user_id);
      setJustCleared({ saved, name: client.name });
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setJustCleared(null), UNDO_SECONDS * 1000);
    } catch (err) {
      toastError('Could not clear ' + (client.name || 'this client') + ': ' + (err.message || 'try again'));
    } finally {
      setBusy(null);
    }
  };
  const onUndo = async () => {
    const { saved } = justCleared;
    setJustCleared(null);
    try { await undo(saved); } catch (err) { toastError('Could not undo: ' + (err.message || 'try again')); }
  };

  return (
    <section aria-label="Clients who need you" style={{ marginBottom: '24px' }}>
      <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ color: c.muted, fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase' }}>
          Needs you
        </span>
        {rows.length > 0 && (
          <span style={{ backgroundColor: 'rgba(212,175,55,0.15)', color: c.gold, borderRadius: '8px', padding: '2px 8px', fontSize: '11px', fontWeight: 700 }}>
            {rows.length}
          </span>
        )}
        <div style={{ flex: 1, height: '1px', backgroundColor: c.border }} />
      </div>

      {justCleared && (
        <div role="status" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', padding: '10px 14px', borderRadius: '8px', backgroundColor: c.card, border: `1px solid ${c.border}`, color: c.second, fontSize: '13px' }}>
          <span style={{ flex: 1 }}>Cleared {justCleared.name}. They come back when something new happens.</span>
          <button type="button" onClick={onUndo} style={{ background: 'none', border: 'none', color: c.gold, fontWeight: 700, fontSize: '13px', cursor: 'pointer', padding: '4px 6px', fontFamily: 'inherit' }}>
            Undo
          </button>
        </div>
      )}

      {failed && (
        <div style={{ marginBottom: '8px', padding: '10px 14px', borderRadius: '8px', border: `1px solid ${c.danger}`, color: c.danger, fontSize: '13px' }}>
          Could not check who needs you.{' '}
          <button type="button" onClick={() => reload()} style={{ background: 'none', border: 'none', color: c.danger, textDecoration: 'underline', cursor: 'pointer', fontSize: '13px', padding: 0, fontFamily: 'inherit' }}>
            Try again
          </button>
        </div>
      )}

      {loading && !rows.length && !failed && (
        <div style={{ padding: '4px 0 8px', color: c.muted, fontSize: '13px' }}>Checking who needs you…</div>
      )}

      {!loading && !failed && rows.length === 0 && (
        <div style={{ color: c.success, fontSize: '13px', padding: '10px 14px', borderRadius: '8px', backgroundColor: 'rgba(52,199,89,0.06)', border: '1px solid rgba(52,199,89,0.15)' }}>
          ✓ Nobody needs you right now
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {rows.map(({ client, reasons }) => (
          <div key={client.auth_user_id} style={{
            display: 'flex', alignItems: 'stretch', gap: '8px',
            backgroundColor: c.card, border: `1px solid ${c.border}`, borderLeft: `3px solid ${c.gold}`, borderRadius: '14px',
          }}>
            <button
              type="button"
              onClick={() => onOpen?.(client, tabForReasons(reasons))}
              style={{ flex: 1, minWidth: 0, textAlign: 'left', background: 'none', border: 'none', padding: '12px 14px', cursor: 'pointer', color: c.text, fontFamily: 'inherit' }}
            >
              <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '4px' }}>{client.name}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
                {reasons.map((r) => (
                  <span key={r.kind} style={{ fontSize: '13px', color: r.kind === 'idle' ? c.muted : c.second }}>
                    <span aria-hidden="true">{reasonIcon[r.kind]} </span>{r.text}
                  </span>
                ))}
              </div>
            </button>
            <button
              type="button"
              onClick={() => onClear(client)}
              disabled={busy === client.auth_user_id}
              aria-label={'Clear ' + (client.name || 'this client')}
              style={{
                alignSelf: 'center', marginRight: '12px', padding: '8px 14px', borderRadius: '8px',
                background: 'transparent', border: `1px solid ${c.border}`, color: c.muted,
                fontSize: '13px', fontWeight: 600, cursor: busy === client.auth_user_id ? 'wait' : 'pointer', fontFamily: 'inherit', flexShrink: 0,
              }}
            >
              {busy === client.auth_user_id ? 'Clearing…' : 'Clear'}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};

export default NeedsYouGroup;
